<template>
  <section class="dashboard-panel" aria-labelledby="reports-panel-title">
    <header class="dashboard-panel-header">
      <div>
        <h3 id="reports-panel-title" class="dashboard-panel-title">
          {{ s__('ReportConstructor|Report Constructor') }}
        </h3>
      </div>
    </header>

    <div class="dashboard-panel-body report-constructor">
      <div v-if="loadingOptions" class="muted">{{ __('Loading…') }}</div>
      <div v-else-if="optionsError" class="error">{{ optionsError }}</div>
      <div v-else>
        <div v-if="showCustomReportSetupHint" class="custom-report-setup-hint" role="note">
          <p class="muted custom-report-setup-hint-text">
            {{
              s__(
                'ReportConstructor|You can save filters, ordering and groupping criteria for My Custom Report in Profile settings. After that, My Report will appear as a scope option below.'
              )
            }}
          </p>
          <p class="custom-report-setup-hint-cta">
            <a :href="customReportSettingsProfileUrl" class="custom-report-setup-hint-link">{{
              s__('ReportConstructor|Open My Custom Report settings')
            }}</a>
          </p>
        </div>

        <div v-if="!previewResult" class="wizard">
          <div v-if="showScopeStep" class="row">
            <div>
              <div class="step-label-row">
                <label>{{ s__('Report|Choose report scope:') }}</label>
                <button
                  type="button"
                  class="info-icon"
                  :title="s__('ReportConstructor|Open help')"
                  @click="openInfo('scope')"
                >
                  <InfoIcon />
                </button>
              </div>
              <select v-model="selectedScope" @change="handleScopeChange">
                <option value="">{{ __('Select...') }}</option>
                <option v-for="scope in scopes" :key="scope" :value="scope">
                  {{ scopeLabel(scope) }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="showProjectStep" class="row">
            <div>
              <div class="step-label-row">
                <label>{{ s__('Report|Choose project:') }}</label>
                <button
                  type="button"
                  class="info-icon"
                  :title="s__('ReportConstructor|Open help')"
                  @click="openInfo('project')"
                >
                  <InfoIcon />
                </button>
              </div>
              <select v-model="selectedProjectId">
                <option value="">{{ __('Select...') }}</option>
                <option v-for="project in selectableProjects" :key="project.id" :value="project.id">
                  {{ project.title }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="showTypeStep" class="row">
            <div>
              <div class="step-label-row">
                <label>{{ s__('Report|Choose report type:') }}</label>
                <button
                  type="button"
                  class="info-icon"
                  :title="s__('ReportConstructor|Open help')"
                  @click="openInfo('type')"
                >
                  <InfoIcon />
                </button>
              </div>
              <select v-model="selectedReportKind" @change="handleReportKindChange">
                <option value="">{{ __('Select...') }}</option>
                <option v-for="kind in availableReportKinds" :key="kind" :value="kind">
                  {{ reportKindLabel(kind) }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="showPeriodStep" class="row">
            <div>
              <div class="step-label-row">
                <label>{{ s__('Report|Choose report period:') }}</label>
                <button
                  type="button"
                  class="info-icon"
                  :title="s__('ReportConstructor|Open help')"
                  @click="openInfo('period')"
                >
                  <InfoIcon />
                </button>
              </div>
              <select v-model="selectedPeriod">
                <option value="">{{ __('Select...') }}</option>
                <option v-for="period in availablePeriods" :key="period" :value="period">
                  {{ periodLabel(period) }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-actions mobile-stretch-btn-group">
            <button
              class="btn-primary"
              :disabled="!canGenerate || generating"
              @click="generatePreview"
            >
              <span v-if="generating">{{ __('Loading…') }}</span>
              <span v-else>{{ __('Generate') }}</span>
            </button>
          </div>
          <div v-if="generationError" class="error">{{ generationError }}</div>
        </div>

        <div v-else class="result">
          <div class="result-header">
            <h3>{{ __('Report') }}</h3>
            <div class="actions result-header-actions mobile-stretch-btn-group">
              <button
                type="button"
                class="btn-secondary btn-sm"
                :disabled="downloadingCsv"
                @click="downloadCsv"
              >
                <span v-if="downloadingCsv">{{ __('Loading…') }}</span>
                <span v-else>{{ __('Download CSV') }}</span>
              </button>
              <button type="button" class="btn-secondary btn-sm" @click="resetWizard">
                <span>{{ __('New Report') }}</span>
              </button>
            </div>
          </div>

          <p class="muted">{{ previewSummary }}</p>

          <TaskPreviewTable
            v-if="isTaskPreview"
            ref="taskTableRef"
            :rows="previewResult.rows || []"
            :project-scoped="previewResult.project_scoped === true"
            :grouped="true"
            :swimlane-mode="previewResult.swimlane_mode || ''"
            :empty-text="__('No data')"
            :clickable-labels="true"
            :clickable-personal-labels="true"
            :chat-id="chatId"
            :from-id="fromId"
            @table-scroll="onTableWrapScroll"
            @label-click="openLabelTasksModal"
            @personal-label-click="openPersonalLabelTasksModal"
          />
          <div v-else ref="tableWrapRef" class="table-wrap" @scroll="onTableWrapScroll">
            <table class="report-table">
              <thead>
                <tr>
                  <th v-for="column in previewResult.columns" :key="column">
                    {{ columnHeader(column) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="renderedRows.length === 0">
                  <td :colspan="visibleColumnCount" class="empty">{{ __('No data') }}</td>
                </tr>
                <tr
                  v-for="row in renderedRows"
                  :key="row.__key"
                  :class="{ 'group-separator-row': row.__group }"
                >
                  <template v-if="row.__group">
                    <td :colspan="visibleColumnCount" class="group-separator-cell">
                      <div class="group-separator-content">
                        <span class="group-separator-prefix muted">{{ row.prefix }}</span>
                        <strong class="group-separator-value">{{ row.value }}</strong>
                      </div>
                    </td>
                  </template>
                  <template v-else>
                    <td v-for="column in previewResult.columns" :key="column">
                      {{ formatCell(row[column]) }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="downloadError" class="error">{{ downloadError }}</div>
        </div>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <div
      v-if="showStickyScrollbar"
      ref="stickyScrollbarRef"
      class="sticky-h-scroll"
      :style="{ left: `${stickyViewportLeft}px`, width: `${stickyViewportWidth}px` }"
      @scroll="onStickyScrollbarScroll"
    >
      <div class="sticky-h-scroll-inner" :style="{ width: `${stickyScrollbarInnerWidth}px` }"></div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="infoModalKey" class="modal-overlay" @click.self="closeInfo">
      <div class="modal-content info-modal">
        <div class="modal-header">
          <h3>{{ infoModalTitle }}</h3>
          <button type="button" class="modal-close" :aria-label="__('Close')" @click="closeInfo">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <p class="muted">{{ infoModalText }}</p>
          <p v-if="infoModalKey === 'scope'" class="muted info-modal-settings">
            <a
              :href="customReportSettingsProfileUrl"
              class="info-modal-settings-link"
              @click="closeInfo"
            >
              {{ s__('ReportConstructor|Open My Custom Report settings') }}
            </a>
          </p>
        </div>

        <div class="modal-footer mobile-stretch-btn-group">
          <button class="btn-primary" @click="closeInfo">
            {{ __('Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <LabelTasksModal
    :visible="!!selectedLabel"
    :label="selectedLabel"
    :scope="selectedLabelScope"
    :project-id="selectedLabel?.projectId || selectedLabel?.project_id || props.projectId"
    :chat-id="chatId"
    :from-id="fromId"
    @label-click="openScopedLabelTasksModal"
    @close="closeLabelTasksModal"
  />
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { __, s__ } from '@/locale'
import InfoIcon from '../common/icons/InfoIcon.vue'
import CloseIcon from '../common/icons/CloseIcon.vue'
import TaskPreviewTable from '../common/TaskPreviewTable.vue'
import LabelTasksModal from '../common/LabelTasksModal.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  projectId: { type: String, default: null },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
})

const OPTIONS_QUERY = gql`
  query ReportConstructorOptions($projectId: ID) {
    reportConstructorOptions(projectId: $projectId)
  }
`

const PREVIEW_MUTATION = gql`
  mutation PreviewReportConstructor(
    $scope: String!
    $reportKind: String!
    $period: String
    $projectId: ID
  ) {
    previewReportConstructor(
      scope: $scope
      reportKind: $reportKind
      period: $period
      projectId: $projectId
    ) {
      preview
      errors
    }
  }
`

const EXPORT_CSV_MUTATION = gql`
  mutation ExportReportConstructorCsv(
    $scope: String!
    $reportKind: String!
    $period: String
    $projectId: ID
  ) {
    exportReportConstructorCsv(
      scope: $scope
      reportKind: $reportKind
      period: $period
      projectId: $projectId
    ) {
      csvContent
      filename
      mimeType
      errors
    }
  }
`

const optionsData = ref({})
const loadingOptions = ref(true)
const optionsError = ref('')
const generating = ref(false)
const generationError = ref('')
const downloadingCsv = ref(false)
const downloadError = ref('')
const previewResult = ref(null)
const infoModalKey = ref('')
useBodyScrollLock(computed(() => !!infoModalKey.value))
const tableWrapRef = ref(null)
const taskTableRef = ref(null)
const stickyScrollbarRef = ref(null)
const showStickyScrollbar = ref(false)
const stickyScrollbarInnerWidth = ref(0)
const syncingScroll = ref(false)
const stickyViewportLeft = ref(0)
const stickyViewportWidth = ref(0)
const hasRestoredState = ref(false)

const selectedScope = ref('')
const selectedReportKind = ref('')
const selectedProjectId = ref('')
const selectedPeriod = ref('')
const selectedLabel = ref(null)
const selectedLabelScope = ref('project')

const { load: loadOptions, result: optionsQueryResult } = useLazyQuery(OPTIONS_QUERY, () => ({
  projectId: props.projectId || null,
}))
const { mutate: previewMutate } = useMutation(PREVIEW_MUTATION)
const { mutate: exportCsvMutate } = useMutation(EXPORT_CSV_MUTATION)

function initOptions(raw) {
  optionsData.value = raw || {}
  selectedScope.value = raw?.preselected_scope || ''
  selectedProjectId.value = raw?.preselected_project_id || props.projectId || ''
  restoreConstructorState()
}

function isDashboardPath(pathname) {
  if (!pathname) return false
  if (pathname === '/dashboard') return true
  return /^\/projects\/[^/]+\/dashboard$/.test(pathname)
}

function reportConstructorStateStorageKey() {
  if (typeof window === 'undefined') return null
  if (!isDashboardPath(window.location.pathname)) return null

  const context = props.projectId ? `project:${props.projectId}` : 'personal'
  return `dashboardReportConstructorState:${context}`
}

function persistConstructorState() {
  const storageKey = reportConstructorStateStorageKey()
  if (!storageKey) return

  try {
    const payload = {
      selectedScope: selectedScope.value || '',
      selectedReportKind: selectedReportKind.value || '',
      selectedProjectId: selectedProjectId.value || '',
      selectedPeriod: selectedPeriod.value || '',
      previewResult: previewResult.value || null,
      version: 1,
    }
    sessionStorage.setItem(storageKey, JSON.stringify(payload))
  } catch {
    // no-op: state persistence should not break report constructor
  }
}

function clearConstructorState() {
  const storageKey = reportConstructorStateStorageKey()
  if (!storageKey) return

  try {
    sessionStorage.removeItem(storageKey)
  } catch {
    // no-op: state cleanup should not break report constructor
  }
}

function restoreConstructorState() {
  if (hasRestoredState.value) return

  const storageKey = reportConstructorStateStorageKey()
  if (!storageKey) {
    hasRestoredState.value = true
    return
  }

  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) {
      hasRestoredState.value = true
      return
    }

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      hasRestoredState.value = true
      return
    }

    if (parsed.selectedScope) selectedScope.value = parsed.selectedScope
    if (parsed.selectedReportKind) selectedReportKind.value = parsed.selectedReportKind
    if (parsed.selectedProjectId) selectedProjectId.value = parsed.selectedProjectId
    if (parsed.selectedPeriod) selectedPeriod.value = parsed.selectedPeriod
    if (parsed.previewResult && typeof parsed.previewResult === 'object') {
      previewResult.value = parsed.previewResult
    }
  } catch {
    // no-op: fallback to defaults
  } finally {
    hasRestoredState.value = true
  }
}

loadOptions()
  .catch(err => {
    optionsError.value = err.message || __('Failed to load options')
  })
  .finally(() => {
    loadingOptions.value = false
  })

watch(
  optionsQueryResult,
  value => {
    if (value?.reportConstructorOptions) {
      initOptions(value.reportConstructorOptions)
    }
  },
  { immediate: true }
)

const scopes = computed(() => optionsData.value?.scopes || [])
const hasMyReportScope = computed(() => scopes.value.includes('my_report'))
const showCustomReportSetupHint = computed(() => !hasMyReportScope.value)
const reportTypesMap = computed(() => optionsData.value?.report_types || {})
const availableReportKinds = computed(() => {
  if (!selectedScope.value) return []
  const kinds = reportTypesMap.value?.[selectedScope.value] || []
  if (selectedScope.value !== 'project') return kinds

  const selectedProject = selectableProjects.value.find(
    project => String(project.id) === String(selectedProjectId.value)
  )
  const hasConfiguredReport = selectedProject?.has_configured_report === true

  const filteredKinds = kinds.filter(kind => kind !== 'project_custom' || hasConfiguredReport)
  const projectCustomIndex = filteredKinds.indexOf('project_custom')
  if (projectCustomIndex <= 0) return filteredKinds

  return ['project_custom', ...filteredKinds.filter(kind => kind !== 'project_custom')]
})

const selectableProjects = computed(() => {
  const timeProjects = optionsData.value?.time_projects || []
  const projects = optionsData.value?.projects || []
  if (selectedReportKind.value === 'time') {
    return selectedScope.value === 'project' ? timeProjects : projects
  }
  return projects
})

const availablePeriods = computed(() => {
  const timePeriods = optionsData.value?.time_periods || []
  const taskPeriods = optionsData.value?.task_periods || {}
  if (!selectedScope.value || !selectedReportKind.value) return []
  if (selectedScope.value === 'my_report') return []
  if (selectedReportKind.value === 'project_custom') return []
  if (selectedReportKind.value === 'time') return timePeriods
  return taskPeriods?.[selectedScope.value] || []
})

const showScopeStep = computed(
  () => !(selectedScope.value === 'project' && !!selectedProjectId.value && !!props.projectId)
)
const showTypeStep = computed(() => !!selectedScope.value && selectedScope.value !== 'my_report')
const showProjectStep = computed(
  () => selectedScope.value === 'project' && (!selectedProjectId.value || !props.projectId)
)
const showPeriodStep = computed(
  () =>
    !!selectedScope.value &&
    !!selectedReportKind.value &&
    selectedScope.value !== 'my_report' &&
    selectedReportKind.value !== 'project_custom' &&
    (selectedScope.value !== 'project' || !!selectedProjectId.value)
)

const canGenerate = computed(() => {
  if (!selectedScope.value || !selectedReportKind.value) return false
  if (selectedScope.value === 'project' && !selectedProjectId.value) return false
  if (
    selectedScope.value !== 'my_report' &&
    selectedReportKind.value !== 'project_custom' &&
    !selectedPeriod.value
  ) {
    return false
  }
  return true
})

const previewSummary = computed(() => {
  if (!previewResult.value) return ''
  const total = previewResult.value.total_count || 0
  const limit = previewResult.value.preview_limit || 250
  if (previewResult.value.truncated) {
    return interpolate(s__('ReportConstructor|Showing first %{limit} of %{total}'), {
      limit,
      total,
    })
  }
  return interpolate(s__('ReportConstructor|Showing %{total}'), { total })
})

const isTaskPreview = computed(
  () => previewResult.value?.report_kind === 'tasks' && selectedReportKind.value !== 'time'
)

const renderedRows = computed(() => {
  if (!previewResult.value) return []
  return (previewResult.value.rows || []).map((row, index) => ({
    ...row,
    __key: `row-${index}`,
  }))
})

const visibleColumnCount = computed(() => {
  if (!previewResult.value) return 1
  return previewResult.value.columns.length
})

const columnLabelMap = {
  id: s__('ReportConstructor|ID'),
  title: s__('ReportConstructor|Title'),
  project: s__('ReportConstructor|Project'),
  assignee: s__('ReportConstructor|Assignee'),
  author: s__('ReportConstructor|Author'),
  status: s__('ReportConstructor|Status'),
  priority: s__('ReportConstructor|Priority'),
  labels: s__('ReportConstructor|Labels'),
  due_date: s__('ReportConstructor|Due date'),
  finished_at: s__('ReportConstructor|Finished at'),
  bucket: s__('ReportConstructor|Bucket'),
  swimlane: s__('ReportConstructor|Swimlane'),
  date: s__('ReportConstructor|Date'),
  task_id: s__('ReportConstructor|Task ID'),
  task_title: s__('ReportConstructor|Task title'),
  duration_minutes: s__('ReportConstructor|Duration (minutes)'),
  description: s__('ReportConstructor|Description'),
  user: s__('ReportConstructor|User'),
}

function handleScopeChange() {
  selectedPeriod.value = ''
  if (selectedScope.value !== 'project') selectedProjectId.value = ''
  if (selectedScope.value !== 'my_report') {
    selectedReportKind.value = ''
  }
}

watch(
  () => selectedScope.value,
  scope => {
    if (scope === 'my_report') {
      selectedReportKind.value = 'custom'
    }
  },
  { immediate: true }
)

watch(
  () => availableReportKinds.value,
  kinds => {
    if (selectedReportKind.value && !kinds.includes(selectedReportKind.value)) {
      selectedReportKind.value = ''
      selectedPeriod.value = ''
    }
  }
)

function handleReportKindChange() {
  selectedPeriod.value = ''
}

async function generatePreview() {
  generationError.value = ''
  generating.value = true
  try {
    const res = await previewMutate({
      scope: selectedScope.value,
      reportKind: selectedReportKind.value,
      period: selectedPeriod.value || null,
      projectId: selectedProjectId.value || null,
    })
    const payload = res?.data?.previewReportConstructor
    if (payload?.errors?.length) {
      generationError.value = payload.errors.join(', ')
      return
    }
    previewResult.value = payload.preview
    persistConstructorState()
  } catch (err) {
    generationError.value = err.message || __('Failed to generate report')
  } finally {
    generating.value = false
  }
}

async function downloadCsv() {
  downloadError.value = ''
  downloadingCsv.value = true
  try {
    const res = await exportCsvMutate({
      scope: selectedScope.value,
      reportKind: selectedReportKind.value,
      period: selectedPeriod.value || null,
      projectId: selectedProjectId.value || null,
    })
    const payload = res?.data?.exportReportConstructorCsv
    if (payload?.errors?.length) {
      downloadError.value = payload.errors.join(', ')
      return
    }
    const csvContent = payload?.csvContent || ''
    const filename = payload?.filename || 'report.csv'
    const blob = new Blob([`\uFEFF${csvContent}`], { type: payload?.mimeType || 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (err) {
    downloadError.value = err.message || __('Failed to export CSV')
  } finally {
    downloadingCsv.value = false
  }
}

function resetWizard() {
  previewResult.value = null
  showStickyScrollbar.value = false
  stickyScrollbarInnerWidth.value = 0
  clearConstructorState()
}

function scopeLabel(scope) {
  if (scope === 'my_report') return s__('Report|My Report')
  if (scope === 'project') return s__('Report|Project Report')
  return s__('Report|Personal Report')
}

function reportKindLabel(kind) {
  if (kind === 'time') return s__('Report|Time Report')
  if (kind === 'project_custom') return s__('Report|Configured Project Report')
  if (kind === 'custom') return s__('Report|Tasks Report')
  return s__('Report|Tasks Report')
}

function periodLabel(period) {
  const map = {
    unfinished: s__('Report|My Unfinished Tasks'),
    deadline_3d: s__('Report|Deadline (3 days)'),
    deadline_7d: s__('Report|Next Week'),
    completed_week: s__('Report|Previous Week'),
    completed_month: s__('Report|Previous Month'),
    week: s__('Report|Previous Week'),
    month: s__('Report|Previous Month'),
  }
  return map[period] || period
}

function columnHeader(column) {
  return columnLabelMap[column] || column.replaceAll('_', ' ')
}

function formatCell(value) {
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

function openInfo(key) {
  infoModalKey.value = key
}

function closeInfo() {
  infoModalKey.value = ''
}

function openLabelTasksModal(label) {
  selectedLabelScope.value = 'project'
  selectedLabel.value = label
}

function openPersonalLabelTasksModal(label) {
  selectedLabelScope.value = 'personal'
  selectedLabel.value = label
}

function openScopedLabelTasksModal(payload) {
  if (!payload?.label) return
  selectedLabelScope.value = payload.scope || 'project'
  selectedLabel.value = payload.label
}

function closeLabelTasksModal() {
  selectedLabel.value = null
}

const infoModalTitle = computed(() => {
  const map = {
    scope: s__('ReportConstructor|About Scope'),
    type: s__('ReportConstructor|About Report Type'),
    project: s__('ReportConstructor|About Project Selection'),
    period: s__('ReportConstructor|About Period'),
  }
  return map[infoModalKey.value] || ''
})

const customReportSettingsProfileUrl = computed(() => {
  const params = new URLSearchParams()
  params.set('settings_section', 'customReport')
  if (props.chatId) params.set('chat_id', props.chatId)
  if (props.fromId) params.set('from_id', props.fromId)
  return `/profile?${params.toString()}`
})

const infoModalText = computed(() => {
  const map = {
    scope: s__(
      'ReportConstructor|Choose Personal for your involved tasks, Project for all tasks in one project, or My Report for your saved custom configuration.'
    ),
    type: s__(
      'ReportConstructor|Tasks Report shows task lists and status-related data. Time Report shows logged time entries.'
    ),
    project: s__(
      'ReportConstructor|Select the project for project-scoped reports. For Time Report, only projects with enabled time tracking are available.'
    ),
    period: s__(
      'ReportConstructor|Period defines the report window: upcoming deadlines, previous week/month, or unfinished tasks depending on report type.'
    ),
  }
  return map[infoModalKey.value] || ''
})

function interpolate(template, values) {
  return Object.keys(values).reduce(
    (acc, key) => acc.replace(`%{${key}}`, String(values[key])),
    template
  )
}

function updateStickyScrollbar() {
  const wrap = isTaskPreview.value ? taskTableRef.value?.tableWrapElement : tableWrapRef.value
  if (!wrap) return

  const wrapRect = wrap.getBoundingClientRect()
  const isInViewport = wrapRect.bottom > 0 && wrapRect.top < window.innerHeight
  const hasHorizontalOverflow = wrap.scrollWidth > wrap.clientWidth + 1
  showStickyScrollbar.value = hasHorizontalOverflow && isInViewport
  stickyScrollbarInnerWidth.value = wrap.scrollWidth
  const left = Math.max(8, Math.round(wrapRect.left))
  const maxWidth = Math.max(0, window.innerWidth - left - 8)
  stickyViewportLeft.value = left
  stickyViewportWidth.value = Math.min(Math.round(wrapRect.width), maxWidth)

  if (!showStickyScrollbar.value) return
  if (syncingScroll.value) return

  nextTick(() => {
    const sticky = stickyScrollbarRef.value
    if (!sticky) return
    if (Math.abs(sticky.scrollLeft - wrap.scrollLeft) < 1) return

    syncingScroll.value = true
    sticky.scrollLeft = wrap.scrollLeft
    requestAnimationFrame(() => {
      syncingScroll.value = false
    })
  })
}

function onTableWrapScroll() {
  const wrap = isTaskPreview.value ? taskTableRef.value?.tableWrapElement : tableWrapRef.value
  const sticky = stickyScrollbarRef.value
  if (!wrap || !sticky) return
  if (syncingScroll.value) return

  syncingScroll.value = true
  sticky.scrollLeft = wrap.scrollLeft
  requestAnimationFrame(() => {
    syncingScroll.value = false
  })
}

function onStickyScrollbarScroll() {
  const wrap = isTaskPreview.value ? taskTableRef.value?.tableWrapElement : tableWrapRef.value
  const sticky = stickyScrollbarRef.value
  if (!wrap || !sticky) return
  if (syncingScroll.value) return

  syncingScroll.value = true
  wrap.scrollLeft = sticky.scrollLeft
  requestAnimationFrame(() => {
    syncingScroll.value = false
  })
}

onMounted(() => {
  window.addEventListener('resize', updateStickyScrollbar)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStickyScrollbar)
})

watch(
  previewResult,
  async value => {
    if (!value) return
    await nextTick()
    updateStickyScrollbar()
  },
  { flush: 'post' }
)

watch(
  loadingOptions,
  async isLoading => {
    if (isLoading || !previewResult.value) return
    await nextTick()
    updateStickyScrollbar()
  },
  { flush: 'post' }
)

watch(
  [selectedScope, selectedReportKind, selectedProjectId, selectedPeriod, previewResult],
  () => {
    persistConstructorState()
  },
  { deep: true }
)
</script>

<style scoped>
.title {
  margin-top: 0;
  margin-bottom: 16px;
}

.wizard {
  width: 100%;
  min-width: 0;
}

.step-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #6b7280;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.info-icon:hover {
  color: #2aabee;
}

.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.result-header-actions {
  margin-top: 0;
  justify-content: flex-end;
}

@media (max-width: 767px) {
  .result-header {
    align-items: flex-start;
    flex-direction: column;
  }
}

.sticky-h-scroll {
  position: fixed;
  bottom: 8px;
  height: 10px;
  z-index: 30;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.sticky-h-scroll-inner {
  height: 8px;
}

.report-constructor,
.result {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.custom-report-setup-hint {
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.custom-report-setup-hint-text {
  margin: 0;
  color: #666;
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
}

.custom-report-setup-hint-cta {
  margin: 8px 0 0;
}

.custom-report-setup-hint-link {
  color: #2aabee;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-normal);
  text-decoration: none;
}

.custom-report-setup-hint-link:hover {
  color: #239ad8;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.info-modal {
  max-width: 520px;
}

.info-modal-settings {
  margin-top: 12px;
  margin-bottom: 0;
}

.info-modal-settings-link {
  color: #2aabee;
  font-weight: 600;
  text-decoration: none;
}

.info-modal-settings-link:hover {
  text-decoration: underline;
}
</style>
